import { useEffect, useRef } from 'react';

export interface FocusTarget {
  /** Whether this field currently has a validation error. */
  isInvalid: boolean;
  /**
   * Ref to the field's focusable element, or to a wrapper around it for
   * components that don't forward a ref (the first focusable descendant is
   * focused).
   */
  inputRef: React.RefObject<HTMLElement>;
}

const FOCUSABLE_SELECTOR =
  'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getFocusTarget(element: HTMLElement | null) {
  if (!element || element.matches(FOCUSABLE_SELECTOR)) {
    return element;
  }

  return element.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
}

export interface FocusFirstInvalidFieldProps {
  /**
   * A counter that increments once per submit attempt, regardless of
   * whether that attempt passed validation (e.g. Formik's `submitCount`,
   * react-hook-form's `formState.submitCount`).
   */
  submitCount: number;
  /** Fields to check, in priority order — the first invalid one is focused. */
  fields: FocusTarget[];
  /**
   * `true` while the current attempt is still being validated or submitted
   * (e.g. Formik's `isSubmitting`). Focus waits until it's `false`, so errors
   * left over from a previous attempt (such as a server error set with
   * `setFieldError`) can't steal focus before the current result is known.
   * Once provided, each attempt is settled the first time it's `false`: the
   * first invalid field is focused, or nothing is if the attempt succeeded.
   */
  isPending?: boolean;
}

/**
 * Renders nothing. On each failed submit attempt, moves keyboard focus to
 * the first invalid field (in the order given), so screen reader and
 * keyboard users land on the error instead of losing their place. Keyed off
 * `submitCount` rather than the error values themselves, so it doesn't
 * refight the user while they're still typing a correction.
 *
 * Form-library agnostic — the caller decides what `isInvalid` means for
 * each field (Formik's `touched && errors`, react-hook-form's
 * `formState.errors`, or anything else) and supplies it directly.
 *
 * Exists as its own component (rather than a hook called inline) because a
 * component that itself renders the form provider runs before that
 * provider's context exists — this needs to be rendered as a child of it,
 * alongside your fields.
 */
function FocusFirstInvalidField({
  submitCount,
  fields,
  isPending
}: FocusFirstInvalidFieldProps) {
  const lastHandledSubmitCount = useRef(0);

  useEffect(() => {
    // The form was reset (e.g. Formik's `resetForm` after a successful save),
    // so start counting attempts again from zero.
    if (submitCount < lastHandledSubmitCount.current) {
      lastHandledSubmitCount.current = 0;
    }

    if (submitCount === lastHandledSubmitCount.current || isPending) {
      return;
    }

    const firstInvalid = fields.find(({ isInvalid }) => isInvalid);

    // Without `isPending` there's no way to tell whether validation has
    // finished, so keep waiting for an invalid field rather than settling.
    if (!firstInvalid && isPending === undefined) {
      return;
    }

    if (firstInvalid) {
      getFocusTarget(firstInvalid.inputRef.current)?.focus();
    }

    lastHandledSubmitCount.current = submitCount;
  }, [submitCount, fields, isPending]);

  return null;
}

export default FocusFirstInvalidField;
